"use client";

import { useEffect } from "react";
import { useFingerprintDecision } from "@/hooks/useFingerprintDecision";
import GetAppWithLinkCopy from "@/components/ios-copy-link/GetAppWithLinkCopy";
import { ClientRedirectProps } from "./ClientRedirect.types";
import { RedirectLayout } from "./RedirectLayout";
import { AppPreviewInterstitial } from "./AppPreviewInterstitial";
import { RedirectHtmlFrame } from "./RedirectHtmlFrame";
import { RedirectSpinner } from "./RedirectSpinner";
import { RedirectStatusContent } from "./RedirectStatusContent";
import { getAppDisplay } from "@/lib/utils/redirectFlow/appDisplay";
import { useClickReporter } from "@/hooks/redirectFlow/useClickReporter";
import { useRedirectFlow } from "@/hooks/redirectFlow/useRedirectFlow";

function ClientRedirect({
  originalPath,
  deferredLink,
  appConfig,
  pasteboardToken,
  redirectionUrls,
  serverOs,
  isBotRequest,
  appPreviewDocument,
  shortLinkUrl,
}: ClientRedirectProps) {
  const { shouldSendFingerprintImmediately, os } = useFingerprintDecision(
    appConfig?.enable_copy_link
  );

  const { createClick } = useClickReporter({
    originalPath,
    deferredLink,
    pasteboardToken,
    shortLinkUrl,
    isBotRequest,
  });

  const { redirectionUrl, redirectStatus, attemptRedirect } = useRedirectFlow({
    os,
    serverOs,
    deferredLink,
    redirectionUrls,
    createClick,
  });

  const { appIconSrc, appName } = getAppDisplay(
    (os ?? serverOs) as "ios" | "android" | "web",
    appConfig
  );

  // The interstitial sits between a visitor and an app store, so it only makes
  // sense on mobile. `appPreviewDocument` already carries the server's read of
  // the same conditions; this re-checks them against the OS the client actually
  // detected, which is the one the redirect itself is resolved from.
  const showAppPreview =
    shouldSendFingerprintImmediately === true &&
    (os === "ios" || os === "android") &&
    !isBotRequest &&
    !!appPreviewDocument;

  // Immediate redirect flow: auto-redirect, unless an App Preview interstitial
  // is configured — then wait for the user to tap its CTA instead.
  useEffect(() => {
    if (!shouldSendFingerprintImmediately || !redirectionUrl) return;

    if (showAppPreview) {
      // Deliberately no already-redirected check here, unlike the auto-redirect
      // flow below. That check exists to stop an automatic redirect from
      // looping, and nothing on this path is automatic — the visitor has to tap
      // Continue.
      //
      // Record the click on load, exactly as the auto-redirect flow does, so
      // "click" means the same thing whether or not an interstitial is
      // configured and a visitor who bounces here is still attributable.
      // Nothing waits on this request: the click id reaches /api/link/continue
      // via the cookie its response sets, so the interstitial paints
      // immediately. The Continue tap is a separate engagement signal recorded
      // by /api/link/continue.
      createClick(true);
      return;
    }

    attemptRedirect(redirectionUrl);
  }, [
    shouldSendFingerprintImmediately,
    redirectionUrl,
    showAppPreview,
    createClick,
    attemptRedirect,
  ]);

  // iOS copy-link flow: create click on mount, then show interactive UI
  useEffect(() => {
    if (shouldSendFingerprintImmediately !== false || isBotRequest) return;
    createClick();
  }, [shouldSendFingerprintImmediately, isBotRequest, createClick]);

  const isCopyLinkFlow =
    (os ?? serverOs) === "ios" && !!appConfig?.enable_copy_link;
  const customHtml = isCopyLinkFlow
    ? appConfig?.custom_copy_link_html
    : appConfig?.custom_redirect_html;

  // OS detection runs in an effect, so the first render always lands here.
  const isDetectingOs = !os || shouldSendFingerprintImmediately == null;

  // An empty redirect_url resolves to "" rather than undefined, so it never
  // becomes a "failed" status — there is nowhere to send this visitor and
  // nothing to report, so keep waiting.
  const hasNowhereToGo = !redirectionUrl && !redirectStatus;

  if (isDetectingOs || hasNowhereToGo) {
    if (appPreviewDocument) {
      return <AppPreviewInterstitial html={appPreviewDocument} />;
    }
    if (customHtml) {
      return <RedirectHtmlFrame html={customHtml} />;
    }
    return (
      <RedirectLayout appIconSrc={appIconSrc} appName={appName}>
        <RedirectSpinner />
      </RedirectLayout>
    );
  }

  if (shouldSendFingerprintImmediately === false) {
    return (
      <GetAppWithLinkCopy
        redirectionUrl={redirectionUrl}
        pasteboardToken={os === "ios" ? pasteboardToken : null}
        appIconSrc={appIconSrc}
        appName={appName}
        customHtml={customHtml}
        darkTheme={appConfig?.copy_link_dialog_dark_theme ?? false}
        language={appConfig?.copy_link_dialog_language}
      />
    );
  }

  if (showAppPreview && appPreviewDocument && !redirectStatus) {
    return <AppPreviewInterstitial html={appPreviewDocument} />;
  }

  if (customHtml && !redirectStatus) {
    return <RedirectHtmlFrame html={customHtml} />;
  }

  return (
    <RedirectLayout appIconSrc={appIconSrc} appName={appName}>
      <RedirectStatusContent
        status={redirectStatus}
        os={os}
        redirectionUrls={redirectionUrls}
      />
    </RedirectLayout>
  );
}

export default ClientRedirect;
